import { InternalRefetchQueriesInclude, useMutation } from "@apollo/client";
import { createContext, useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import { ConfigInput } from "types/generated";
import { AccessResult } from "utils/access";

import {
  ADD_ENVIRONMENT_CONFIG,
  DELETE_ENVIRONMENT_CONFIG,
  UPDATE_ENVIRONMENT_CONFIG,
} from "./gql";
import {
  AddStackConfigGqlResponse,
  AddStackConfigGqlVariables,
  UpdateStackConfigGql,
  UpdateStackConfigGqlVariables,
} from "./types";

type EnvironmentContextType = {
  canCreateAndEditVariables: AccessResult;
  canDeleteVariables: AccessResult;
  loading: boolean;
  onCreate: (input: ConfigInput) => Promise<string | undefined>;
  onUpdate: (input: ConfigInput) => Promise<string | undefined>;
  onDelete: (id: string) => Promise<string | undefined>;
  resourceId: string;
};

type EnvironmentContextProviderProps = {
  resourceId: string;
  refetchQueries: InternalRefetchQueriesInclude;
  canCreateAndEditVariables: AccessResult;
  canDeleteVariables: AccessResult;
  children: React.ReactNode;
};

export const EnvironmentContext = createContext<EnvironmentContextType | undefined>(undefined);
EnvironmentContext.displayName = "EnvironmentContext";

const EnvironmentContextProvider = ({
  resourceId,
  refetchQueries,
  children,
  canCreateAndEditVariables,
  canDeleteVariables,
}: EnvironmentContextProviderProps) => {
  const { onError } = useTypedContext(FlashContext);

  const [addConfig, { loading: creating }] = useMutation<
    AddStackConfigGqlResponse,
    Partial<AddStackConfigGqlVariables>
  >(ADD_ENVIRONMENT_CONFIG, {
    refetchQueries,
    variables: { stackId: resourceId },
    onError,
  });
  const [updateConfig, { loading: updating }] = useMutation<
    UpdateStackConfigGql,
    UpdateStackConfigGqlVariables
  >(UPDATE_ENVIRONMENT_CONFIG, {
    refetchQueries,
    variables: { stackId: resourceId },
    onError,
  });

  const [deleteConfig, { loading: deleting }] = useMutation(DELETE_ENVIRONMENT_CONFIG, {
    refetchQueries: refetchQueries,
    variables: { stackId: resourceId },
    onError,
  });

  const loading = creating || updating || deleting;

  const onCreate = useCallback(
    async (input: ConfigInput) => {
      const { data } = await addConfig({
        variables: {
          input: {
            ...input,
            fileMode: null,
          },
        },
      });
      return data?.stackConfigAdd.id;
    },
    [addConfig]
  );

  const onUpdate = useCallback(
    async (input: ConfigInput) => {
      const { data } = await updateConfig({
        variables: {
          input: {
            ...input,
            fileMode: null,
          },
        },
      });
      return data?.stackConfigAdd.id;
    },
    [updateConfig]
  );

  const onDelete = useCallback(
    async (id: string) => {
      const { data } = await deleteConfig({
        variables: {
          configId: id,
        },
      });

      return data?.stackConfigDelete?.id;
    },
    [deleteConfig]
  );

  const contextValue = {
    canCreateAndEditVariables,
    canDeleteVariables,
    loading,
    onCreate,
    onUpdate,
    onDelete,
    resourceId,
  };

  return <EnvironmentContext.Provider value={contextValue}>{children}</EnvironmentContext.Provider>;
};

export default EnvironmentContextProvider;
